import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Upload, FileText, Download, Trash2, Search, CheckCircle2, FolderUp, X as CloseX } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["DPR", "Drawing", "BOQ", "Invoice", "Agreement", "Legal", "Quarry", "Royalty", "WorkOrder", "Other"];

function fmtSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function DocumentsPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ category: "DPR", project_id: "none", description: "", expiry_date: "" });

  // Bulk upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkCategory, setBulkCategory] = useState("DPR");
  const [bulkProject, setBulkProject] = useState("none");
  const [bulkProgress, setBulkProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const bulkInputRef = useRef(null);

  const load = useCallback(() => api.get("/documents").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
  }, [load]);

  const addBulkFiles = (files) => {
    const arr = Array.from(files);
    setBulkFiles((prev) => [...prev, ...arr]);
  };

  const removeBulkFile = (idx) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadBulk = async () => {
    if (bulkFiles.length === 0) return toast.error("No files selected");
    const results = { ok: 0, fail: 0 };
    for (let i = 0; i < bulkFiles.length; i++) {
      const f = bulkFiles[i];
      setBulkProgress((p) => ({ ...p, [i]: "uploading" }));
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", bulkCategory);
      if (bulkProject !== "none") fd.append("project_id", bulkProject);
      try {
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setBulkProgress((p) => ({ ...p, [i]: "done" }));
        results.ok++;
      } catch (e) {
        setBulkProgress((p) => ({ ...p, [i]: "error" }));
        results.fail++;
      }
    }
    toast.success("Bulk upload complete", {
      description: `${results.ok} succeeded, ${results.fail} failed.`,
    });
    load();
    setTimeout(() => {
      setBulkOpen(false);
      setBulkFiles([]);
      setBulkProgress({});
    }, 1500);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      addBulkFiles(e.dataTransfer.files);
      setBulkOpen(true);
    }
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Select a file");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", form.category);
    if (form.project_id && form.project_id !== "none") fd.append("project_id", form.project_id);
    if (form.description) fd.append("description", form.description);
    if (form.expiry_date) fd.append("expiry_date", form.expiry_date);
    try {
      const { data } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const indexed = data.text_length > 0;
      toast.success("Uploaded", {
        description: indexed
          ? `Indexed ${data.text_length.toLocaleString()} chars${data.ocr_used ? " (via OCR)" : ""}, ${data.num_chunks} chunk(s). AI can now answer from it.`
          : "Document uploaded (no text extracted).",
      });
      setOpen(false);
      setForm({ category: "DPR", project_id: "none", description: "", expiry_date: "" });
      load();
    } catch (e) {
      toast.error("Upload failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const filtered = items
    .filter((d) => filter === "all" || d.category === filter)
    .filter((d) => !search || d.original_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="documents-page">
      <PageHeader
        overline="KNOWLEDGE BASE"
        title="Documents"
        description="Upload DPRs, Drawings, BOQs, Invoices, Agreements, Quarry, Royalty & Legal docs. PDF text is indexed for the Second Brain AI."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="documents-bulk-upload-btn"
              onClick={() => setBulkOpen(true)}
              variant="outline"
              className="rounded-sm border-zinc-300 hover:border-orange-600 hover:text-orange-600"
            >
              <FolderUp className="h-4 w-4 mr-2" /> Bulk Upload
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="documents-upload-btn" className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                  <Upload className="h-4 w-4 mr-2" /> Upload Document
                </Button>
              </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading">Upload document</DialogTitle>
                <DialogDescription>PDFs are auto-indexed for AI. Set expiry for permits, agreements, royalty & quarry docs.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="overline">File (PDF preferred for AI indexing)</Label>
                  <Input data-testid="doc-file-input" ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="rounded-sm mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="doc-category-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">Project (optional)</Label>
                    <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                      <SelectTrigger data-testid="doc-project-select" className="rounded-sm mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="overline">Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="overline">Expiry Date (for permits, agreements, royalty, quarry)</Label>
                  <Input
                    data-testid="doc-expiry-input"
                    type="date"
                    value={form.expiry_date}
                    onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    className="rounded-sm mt-1"
                  />
                  <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                    Optional — set to receive automatic reminders in the Compliance calendar.
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="doc-upload-submit" onClick={upload} disabled={uploading} className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) { setBulkFiles([]); setBulkProgress({}); } }}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk upload documents</DialogTitle>
            <DialogDescription>Drop multiple PDFs — they'll all be indexed for AI search. Same category applied to all.</DialogDescription>
          </DialogHeader>

          <div
            data-testid="bulk-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => bulkInputRef.current?.click()}
            className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
              dragActive ? "border-orange-600 bg-orange-50" : "border-zinc-300 bg-zinc-50 hover:border-orange-500"
            }`}
          >
            <FolderUp className="h-10 w-10 mx-auto mb-3 text-zinc-400" />
            <div className="text-sm font-medium text-zinc-700">
              Drop files here or <span className="text-orange-600 underline">browse</span>
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-mono">PDF, DOC, PNG, JPG — multiple allowed</div>
            <input
              ref={bulkInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => addBulkFiles(e.target.files)}
              data-testid="bulk-file-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Category (applied to all)</Label>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger data-testid="bulk-category-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Project (optional)</Label>
              <Select value={bulkProject} onValueChange={setBulkProject}>
                <SelectTrigger data-testid="bulk-project-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {bulkFiles.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-zinc-200 rounded-sm bg-white">
              {bulkFiles.map((f, i) => (
                <div key={i} data-testid={`bulk-file-${i}`} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm">
                  <FileText className="h-4 w-4 text-orange-600" />
                  <div className="flex-1 min-w-0 truncate">{f.name}</div>
                  <span className="font-mono text-[10px] text-zinc-500">{fmtSize(f.size)}</span>
                  {bulkProgress[i] === "uploading" && <span className="text-[10px] font-mono text-blue-600">uploading…</span>}
                  {bulkProgress[i] === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                  {bulkProgress[i] === "error" && <span className="text-[10px] font-mono text-red-600">FAILED</span>}
                  {!bulkProgress[i] && (
                    <button onClick={(e) => { e.stopPropagation(); removeBulkFile(i); }} className="p-0.5 hover:bg-red-50 rounded-sm">
                      <CloseX className="h-3.5 w-3.5 text-zinc-400 hover:text-red-600" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              data-testid="bulk-upload-submit"
              onClick={uploadBulk}
              disabled={bulkFiles.length === 0 || Object.values(bulkProgress).some((v) => v === "uploading")}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm"
            >
              Upload {bulkFiles.length} file{bulkFiles.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="documents-search"
            placeholder="Search filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-zinc-300"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="documents-filter" className="w-48 rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2.5 overline">Document</th>
              <th className="px-4 py-2.5 overline">Category</th>
              <th className="px-4 py-2.5 overline">Size</th>
              <th className="px-4 py-2.5 overline">AI Indexed</th>
              <th className="px-4 py-2.5 overline">Uploaded</th>
              <th className="px-4 py-2.5 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 text-zinc-400 font-mono text-xs">
                No documents. Upload PDFs to enable Second Brain AI answers.
              </td></tr>
            ) : filtered.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-orange-600" />
                    <div>
                      <div className="font-medium">{d.original_name}</div>
                      {d.description && <div className="text-xs text-zinc-500">{d.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[10px] px-2 py-0.5 border border-zinc-300 rounded-sm">{d.category}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-600">{fmtSize(d.file_size)}</td>
                <td className="px-4 py-3">
                  {d.text_length > 0 ? (
                    <span className="flex items-center gap-1 text-xs text-green-700">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {d.text_length.toLocaleString()} chars
                      {d.ocr_used && (
                        <span className="ml-1 font-mono text-[9px] px-1 py-0.5 bg-blue-100 text-blue-700 rounded-sm">OCR</span>
                      )}
                      {d.num_chunks > 0 && (
                        <span className="ml-1 font-mono text-[9px] text-zinc-500">· {d.num_chunks} chunks</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-400 font-mono">no text</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{d.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="p-1.5 hover:bg-zinc-100 rounded-sm inline-flex mr-1">
                    <Download className="h-4 w-4" />
                  </button>
                  <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm inline-flex">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
